import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Bell, Info } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import Layout from "../components/Layout";
import BranchAccessRequestPopup from "../components/BranchAccessRequestPopup";
import { Button } from "../components/ui/button";
import { EmptyState } from "../components/ui/empty-state";
import { useNotifications } from "../hooks/queries";
import { useAuth } from "../hooks/useAuth";
import { markAllRead, markNotificationRead, Notification } from "../api/api";
import { cn } from "@/lib/utils";

// Only reviewers can open the access request popup (GET /branch-requests/:id).
// Older grant/deny result notifications sent to HMPs still carry the link.
function opensBranchRequest(n: Notification, role?: string): boolean {
  return n.related_branch_access_request_id != null && role !== "hmp";
}

// A notification is an "actie" when it points at something the user should
// act on: a vacature warning, a pending mail approval, an access request or
// a Google Sheets sync problem.
function isAction(n: Notification, role?: string): boolean {
  return n.type === "warning" || n.type === "approval_reminder" || n.type === "sync_problem" || opensBranchRequest(n, role);
}

// The /sheets-sync page is admin-only, so KAMs (who also get sync problem
// notifications) get no link to it.
function actionLabel(n: Notification, role?: string): string | null {
  if (opensBranchRequest(n, role)) return "Bekijk verzoek";
  if (n.related_vacature_id) return "Bekijk vacature";
  if (n.type === "approval_reminder") return "Naar goedkeuring";
  if (n.type === "sync_problem" && role === "admin") return "Naar Sheets-synchronisatie";
  return null;
}

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)    return "zojuist";
  if (diff < 3600)  return `${Math.floor(diff / 60)} min geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
  const d = Math.floor(diff / 86400);
  return d === 1 ? "gisteren" : `${d} dagen geleden`;
}

export default function Meldingen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const role = user?.role;
  const { data, isLoading } = useNotifications();
  const notifications: Notification[] = data ?? [];
  const [branchRequestId, setBranchRequestId] = useState<number | null>(null);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  async function markRead(n: Notification) {
    if (n.is_read) return;
    await markNotificationRead(n.id);
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
  }

  async function handleMarkAllRead() {
    await markAllRead();
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
  }

  async function handleOpen(n: Notification) {
    await markRead(n);
    if (opensBranchRequest(n, role)) {
      setBranchRequestId(n.related_branch_access_request_id);
    } else if (n.related_vacature_id) {
      navigate(`/vacature/${n.related_vacature_id}`);
    } else if (n.type === "approval_reminder") {
      navigate("/mail-goedkeuring");
    } else if (n.type === "sync_problem" && role === "admin") {
      navigate("/sheets-sync");
    }
  }

  const actions = unreadCount > 0 ? (
    <Button variant="outline" data-tour="notif-allread" onClick={handleMarkAllRead}>
      <Check /> <span className="hidden sm:inline">Alles als gelezen markeren</span>
    </Button>
  ) : undefined;

  return (
    <Layout title="Meldingen" subtitle={unreadCount > 0 ? `${unreadCount} ongelezen` : "Alles gelezen"} actions={actions}>
      <div className="mx-auto flex max-w-[1000px] flex-col gap-5">
        <p className="flex items-center gap-1.5 text-xs text-waves-muted">
          <Info size={14} className="shrink-0" />
          Gelezen meldingen worden na 7 dagen automatisch verwijderd.
        </p>
        <div data-tour="notif-list" className="flex flex-col gap-3">
          {isLoading ? (
            <div className="py-16 text-center text-sm text-waves-muted">Laden…</div>
          ) : notifications.length === 0 ? (
            <EmptyState icon={<Bell />} title="Geen meldingen" description="Je hebt nog geen meldingen." className="rounded-[16px] border border-waves-line" />
          ) : (
            notifications.map((n, i) => {
              const label = actionLabel(n, role);
              return (
                <div
                  key={n.id}
                  data-tour={i === 0 ? "notif-first" : undefined}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpen(n)}
                  onKeyDown={e => { if (e.key === "Enter") void handleOpen(n); }}
                  className={cn(
                    "flex cursor-pointer items-start gap-3.5 rounded-[16px] border border-waves-line px-5 py-[18px] transition-colors",
                    n.is_read ? "bg-white hover:bg-waves-subtle" : "bg-waves-subtle hover:bg-waves-mist",
                  )}
                >
                  <span className={cn("mt-[7px] h-2 w-2 shrink-0 rounded-full", n.is_read ? "bg-transparent" : "bg-waves-zee")} />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className={cn("text-[15px] leading-snug", n.is_read ? "font-medium" : "font-bold")}>{n.message}</div>
                    <div className="text-xs text-waves-muted">{timeAgo(n.created_at)} · {isAction(n, role) ? "Actie" : "Overig"}</div>
                  </div>
                  {label && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-0.5 hidden shrink-0 sm:inline-flex"
                      onClick={e => { e.stopPropagation(); void handleOpen(n); }}
                    >
                      {label}
                    </Button>
                  )}
                  {!n.is_read && (
                    <button
                      onClick={e => { e.stopPropagation(); void markRead(n); }}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-pill text-waves-muted hover:bg-waves-mist hover:text-waves-ink"
                      aria-label="Markeer als gelezen"
                      title="Markeer als gelezen"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      <BranchAccessRequestPopup requestId={branchRequestId} onClose={() => setBranchRequestId(null)} />
    </Layout>
  );
}
